import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

const TOKEN_KEY = 'tempest.authToken'
const USER_KEY = 'tempest.user'

export interface CurrentUser {
  id: string
  email: string
  fullName: string
  role: string
  storeId?: string
  storeName?: string
  // Mall + capability flags for the management UI. Optional so sessions stored
  // before these were added still parse; components fall back to role-derived
  // helpers (auth/roles) when absent.
  mallId?: string
  mallName?: string
  canSell?: boolean
  canManageMall?: boolean
}

interface AuthState {
  token: string | null
  user: CurrentUser | null
  setSession: (token: string, user: CurrentUser) => void
  clear: () => void
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY))
  const [user, setUser] = useState<CurrentUser | null>(() => {
    const raw = localStorage.getItem(USER_KEY)
    try { return raw ? (JSON.parse(raw) as CurrentUser) : null } catch { return null }
  })

  // Keep localStorage in sync so refreshes don't kick the user out.
  useEffect(() => {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  }, [token])

  useEffect(() => {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user))
    else localStorage.removeItem(USER_KEY)
  }, [user])

  const value = useMemo<AuthState>(
    () => ({
      token,
      user,
      setSession: (t, u) => { setToken(t); setUser(u) },
      clear: () => { setToken(null); setUser(null) }
    }),
    [token, user]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
